/*
 * hp-stairs -- the campaign's stepped line art, drawn in ONCE the first time it
 * scrolls into view, with the words that stand on it fading in across, in step
 * with the line. Then everything is left in place.
 *
 * SHARED. Used by sections/hp-drop-schedule.liquid (both staircases) and
 * sections/hp-campaign-signup.liquid (the one-step line).
 *
 * THE LINE AND THE WORDS MOVE DIFFERENTLY, ON PURPOSE.
 *   the line   DRAWS ALONG ITS OWN PATH, so it visibly steps DOWN -- tread,
 *              riser, tread -- on the hero lockup's measured line curve
 *              (assets/hp-lockup.js, fitted to the reference MP4):
 *              cubic-bezier(.25, .46, .45, .94), 1.19s, after 0.072s.
 *   the words  FADE IN ACROSS, left to right, each group under its own tread
 *              and LOCKED TO THE LINE: its soft edge starts as the line
 *              reaches that tread and finishes as the line leaves it, on the
 *              same eased timeline, so the words arrive with the line above
 *              them and never before it.
 * History worth keeping: a single clip-window WIPE over line AND words swept
 * the whole staircase in as one flat layer, with no step down at all; then
 * words dropping in from behind the line (the lockup's letters, literally)
 * read as busy against a line that is already doing the stepping.
 *
 * Contract:
 *   <hp-stairs class="hp-stairs" data-hp-stairs-targets=".selector">
 *     <span class="hp-stairs__inner">...svg line art...</span>
 *   </hp-stairs>
 * Every <path> and <line> inside draws, in document order, as ONE continuous
 * stroke. Paths may use M / H / V only (absolute). A horizontal run is a
 * TREAD; `data-hp-stairs-targets` selects, within the PARENT, one box per
 * tread in order, and each box fades in across under its tread.
 *
 * FAILS SAFE. Everything is visible by default; only this script hides the line
 * and the words, and only when it can also reveal them. No script, reduced
 * motion, no IntersectionObserver, or the element hidden by a media query:
 * nothing is hidden.
 *
 * IT LEAVES NOTHING BEHIND. When everything has arrived (or a deadline passes
 * -- the preview pane never finishes an animation) every inline style comes
 * off: no dash pattern, no mask.
 *
 * TWO TRIGGERS, because hiding is only safe if revealing cannot silently fail:
 * an IntersectionObserver, and a passive scroll check as a backstop (CLAUDE.md
 * records an observer that never delivered a callback). Both watch the PARENT,
 * which never moves.
 */
(() => {
  if (customElements.get('hp-stairs')) return;

  // The lockup's measured timings (section-hp-full-bleed-image.css).
  const LINE_DELAY = 72;
  const LINE_MS = 1190;
  const LINE_BEZIER = [0.25, 0.46, 0.45, 0.94];
  const LINE_EASE = `cubic-bezier(${LINE_BEZIER.join(', ')})`;
  const DEADLINE = LINE_DELAY + LINE_MS + 600;
  // The width of the words' soft leading edge.
  const FEATHER = 48;

  class HpStairs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.drawn === 'true') return;
      if (!('IntersectionObserver' in window)) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      // A consumer hid the line with a media query.
      if (getComputedStyle(this).display === 'none') return;

      this.segments = this.measure();
      if (!this.segments.length) return;

      this.segments.forEach((seg) => {
        seg.el.style.strokeDasharray = `${seg.length}px ${seg.length}px`;
        seg.el.style.strokeDashoffset = `${seg.length}px`;
      });
      const selector = this.dataset.hpStairsTargets;
      this.targets = selector && this.parentElement ? [...this.parentElement.querySelectorAll(selector)] : [];
      this.targets.forEach((box) => box.classList.add('hp-stairs-pending'));
      this.classList.add('is-armed');

      // Watch the parent: it never moves, and it holds the words as well.
      this.target = this.parentElement || this;
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.draw();
        },
        { threshold: 0.35 }
      );
      this.observer.observe(this.target);

      this.onScroll = () => {
        const rect = this.target.getBoundingClientRect();
        const view = window.innerHeight || document.documentElement.clientHeight;
        const visible = Math.min(rect.bottom, view) - Math.max(rect.top, 0);
        if (rect.height > 0 && visible >= rect.height * 0.35) this.draw();
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.setTimeout(() => this.onScroll && this.onScroll(), 0);
    }

    disconnectedCallback() {
      this.teardown();
      this.settle();
    }

    teardown() {
      if (this.observer) this.observer.disconnect();
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      this.observer = null;
      this.onScroll = null;
    }

    /* Every stroke, in order, with its length and its TREADS: where each
       horizontal run starts and ends, as distances along the stroke.

       A stretched staircase SVG (`preserveAspectRatio="none"`) is first
       rewritten in PIXELS: a dash pattern is measured in user units, and with
       a non-uniform scale a riser's units are not a tread's, so the drawing
       would crawl down the risers and race along the treads. With the viewBox
       set to the box's own size the scale is 1 in both directions -- and the
       shape is unchanged at any later size, because the box still stretches
       it exactly as before. */
    measure() {
      const segments = [];
      this.querySelectorAll('svg').forEach((svg) => {
        const rect = svg.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const vb = svg.viewBox && svg.viewBox.baseVal;
        const stretched = vb && vb.width && svg.getAttribute('preserveAspectRatio') === 'none';
        const sx = stretched ? rect.width / vb.width : 1;
        const sy = stretched ? rect.height / vb.height : 1;
        if (stretched) svg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);

        svg.querySelectorAll('path, line').forEach((el) => {
          const seg = { el, length: 0, treads: [] };

          if (el.tagName.toLowerCase() === 'path') {
            const tokens = (el.getAttribute('d') || '').match(/[MHV]|-?[\d.]+/g) || [];
            let x = 0;
            let y = 0;
            let d = '';
            for (let i = 0; i < tokens.length; i++) {
              const cmd = tokens[i];
              if (cmd === 'M') {
                x = +tokens[++i] * sx;
                y = +tokens[++i] * sy;
                d += `M${x} ${y}`;
              } else if (cmd === 'H') {
                const nx = +tokens[++i] * sx;
                const from = seg.length;
                seg.length += Math.abs(nx - x);
                x = nx;
                d += `H${x}`;
                seg.treads.push({ from, to: seg.length });
              } else if (cmd === 'V') {
                const ny = +tokens[++i] * sy;
                seg.length += Math.abs(ny - y);
                y = ny;
                d += `V${y}`;
              }
            }
            if (stretched) el.setAttribute('d', d);
          } else {
            seg.length = el.getTotalLength();
            if (el.getAttribute('y1') === el.getAttribute('y2')) {
              seg.treads.push({ from: 0, to: seg.length });
            }
          }
          if (seg.length > 0) segments.push(seg);
        });
      });
      return segments;
    }

    draw() {
      if (this.dataset.drawn === 'true') return;
      this.teardown();
      this.dataset.drawn = 'true';

      const total = this.segments.reduce((sum, seg) => sum + seg.length, 0);
      const anims = [];
      const treads = [];
      let start = 0;

      // One eased timeline for the whole stroke. Each segment holds, draws
      // across ITS share of the eased progress, then holds -- so the drawing
      // runs continuously from one segment into the next on a single curve.
      this.segments.forEach((seg) => {
        const from = start / total;
        const to = (start + seg.length) / total;
        anims.push(
          seg.el.animate(
            [
              { offset: 0, strokeDashoffset: `${seg.length}px` },
              { offset: from, strokeDashoffset: `${seg.length}px` },
              { offset: to, strokeDashoffset: '0px' },
              { offset: 1, strokeDashoffset: '0px' },
            ],
            {
              duration: LINE_MS,
              delay: LINE_DELAY,
              easing: LINE_EASE,
              fill: 'both',
            }
          )
        );
        seg.treads.forEach((tread) => treads.push({ from: (start + tread.from) / total, to: (start + tread.to) / total }));
        start += seg.length;
      });

      // The words. Each box fades in across under ITS tread: a mask whose
      // soft edge travels left to right while the line crosses that tread,
      // on the line's own keyframe offsets, duration and easing -- so the two
      // are locked together whatever the curve. A MASK, not a clip-path, for
      // the soft edge; on the box, which never moves.
      //
      // The mask image is [box black][feather][box transparent]; sliding it
      // from -(W + F) to 0 moves the edge from just before the box's left to
      // its right, so the words start appearing as the line starts the tread
      // and are complete as it leaves.
      this.targets.forEach((box, i) => {
        box.classList.remove('hp-stairs-pending');
        const tread = treads[Math.min(i, treads.length - 1)];
        const width = box.getBoundingClientRect().width;
        if (!tread || !width) return;
        const hidden = `${-(width + FEATHER)}px 0px`;
        const image = `linear-gradient(to right, #000 ${width}px, transparent ${width + FEATHER}px)`;
        const size = `${2 * width + FEATHER}px 100%`;
        box.style.webkitMaskImage = image;
        box.style.maskImage = image;
        box.style.webkitMaskSize = size;
        box.style.maskSize = size;
        box.style.webkitMaskRepeat = 'no-repeat';
        box.style.maskRepeat = 'no-repeat';
        anims.push(
          box.animate(
            [
              { offset: 0, maskPosition: hidden, webkitMaskPosition: hidden },
              { offset: tread.from, maskPosition: hidden, webkitMaskPosition: hidden },
              { offset: tread.to, maskPosition: '0px 0px', webkitMaskPosition: '0px 0px' },
              { offset: 1, maskPosition: '0px 0px', webkitMaskPosition: '0px 0px' },
            ],
            { duration: LINE_MS, delay: LINE_DELAY, easing: LINE_EASE, fill: 'both' }
          )
        );
      });

      this.anims = anims;
      Promise.all(anims.map((a) => a.finished)).then(() => this.settle(), () => {});
      this.settleTimer = window.setTimeout(() => this.settle(), DEADLINE + 1000);
    }

    // Back to plain markup: no dash, no mask, no class.
    settle() {
      window.clearTimeout(this.settleTimer);
      (this.anims || []).forEach((a) => a.cancel());
      this.anims = [];
      (this.segments || []).forEach((seg) => {
        seg.el.style.removeProperty('stroke-dasharray');
        seg.el.style.removeProperty('stroke-dashoffset');
      });
      (this.targets || []).forEach((box) => {
        box.classList.remove('hp-stairs-pending');
        ['mask-image', 'mask-size', 'mask-repeat', '-webkit-mask-image', '-webkit-mask-size', '-webkit-mask-repeat'].forEach(
          (prop) => box.style.removeProperty(prop)
        );
      });
      this.classList.remove('is-armed');
    }
  }

  customElements.define('hp-stairs', HpStairs);
})();
